
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

export type ToastProps = {
  id: string;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  variant?: "default" | "destructive";
  duration?: number;
}

type ToastState = {
  toasts: ToastProps[];
  addToast: (toast: Omit<ToastProps, "id">) => void;
  dismissToast: (id: string) => void;
}

export const useToast = create<ToastState>((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = uuidv4();
    set((state) => ({
      toasts: [
        ...state.toasts,
        { id, ...toast },
      ],
    }));

    // Auto-dismiss toast after duration
    if (toast.duration !== Infinity) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, toast.duration || 5000); // Default 5 seconds
    }
  },
  dismissToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));

// Create a function type that matches how we want to call toast
type ToastFunction = {
  (props: Omit<ToastProps, "id">): void;
  default: (props: Omit<ToastProps, "id">) => void;
  destructive: (props: Omit<ToastProps, "id">) => void;
  dismiss: (id: string) => void;
};

// Create and configure the toast function
const toastFunction = ((props: Omit<ToastProps, "id">) => {
  useToast.getState().addToast({ ...props, variant: "default" });
}) as ToastFunction;

// Add the methods to the function
toastFunction.default = (props: Omit<ToastProps, "id">) => {
  useToast.getState().addToast({ ...props, variant: "default" });
};

toastFunction.destructive = (props: Omit<ToastProps, "id">) => {
  useToast.getState().addToast({ ...props, variant: "destructive" });
};

toastFunction.dismiss = (id: string) => {
  useToast.getState().dismissToast(id);
};

// Export the toast function
export const toast = toastFunction;
